import { Reducer } from "redux";

import {
    FORUM_DELETE_TOPIC_API_SUCCESS,
    ForumDeleteTopicApiSuccessAction,
    TOPIC_FETCH_FAILURE,
    TOPIC_FETCH_REQUEST,
    TOPIC_FETCH_SUCCESS,
    TOPIC_SET_AS_FAVORITE_FAILURE,
    TOPIC_SET_AS_FAVORITE_REQUEST,
    TOPIC_SET_AS_FAVORITE_SUCCESS,
    TOPIC_SUBSCRIBE_SUCCESS,
    TOPIC_UNSUBSCRIBE_SUCCESS,
    TopicFetchFailureAction,
    TopicFetchRequestAction,
    TopicFetchSuccessAction,
    TopicSetAsFavoriteFailureAction,
    TopicSetAsFavoriteRequestAction,
    TopicSetAsFavoriteSuccessAction,
    TopicSubscribeSuccessAction,
    TopicUnsubscribeSuccessAction,
} from "@/mk2/apps/forum/containers/Topic/Topic.actions";
import { tupdate } from "@/mk2/helpers/types";
import infiniteList, {
    State as InfiniteListState,
    initialState as initialInfiniteListState,
} from "@/mk2/reducers/infiniteList";

const postsReducer = infiniteList<
    TopicFetchRequestAction,
    TopicFetchSuccessAction,
    TopicFetchFailureAction
>("TOPIC_FETCH", (a) => a.response.result.posts);

export const hashtagsAndQueryKey = (hashtags, query): string => `${hashtags}:${query || ""}`;

interface TopicPostsState {
    [hashtagsAndQuery: string]: InfiniteListState;
}

export const initialPostsState: InfiniteListState = initialInfiniteListState;

export interface TopicState {
    posts: TopicPostsState;
    errorMessage: string;
    isSubscribed: boolean;
    isFavorite: boolean;
    brandDaysCampaignId: number;
    brandDaysCampaignGreetingText: string;
    counsellingId: number;
    counsellingPostsCount: number | null;
    subscribers: number;
    activeBrandDaysCampaignId: number;
    activeBrandDaysPostsIds: number[];
}

export const initialTopicState: TopicState = {
    posts: {},
    errorMessage: null,
    isSubscribed: false,
    isFavorite: false,
    brandDaysCampaignId: null,
    brandDaysCampaignGreetingText: null,
    counsellingId: null,
    counsellingPostsCount: null,
    subscribers: 0,
    activeBrandDaysCampaignId: null,
    activeBrandDaysPostsIds: [],
};

declare type TopicReducerAction =
    | TopicFetchRequestAction
    | TopicFetchSuccessAction
    | TopicFetchFailureAction
    | TopicSubscribeSuccessAction
    | TopicUnsubscribeSuccessAction
    | ForumDeleteTopicApiSuccessAction
    | TopicSetAsFavoriteRequestAction
    | TopicSetAsFavoriteSuccessAction
    | TopicSetAsFavoriteFailureAction;

const topicReducer: Reducer<TopicState, TopicReducerAction> = (
    state = initialTopicState,
    action,
) => {
    let hashtags;
    let key;
    switch (action.type) {
        case TOPIC_FETCH_REQUEST:
        case TOPIC_FETCH_FAILURE:
            hashtags = action.hashtags ? action.hashtags.join(",") : "";
            key = hashtagsAndQueryKey(hashtags, action.query);
            return {
                ...state,
                posts: {
                    ...state.posts,
                    [key]: postsReducer(state.posts[key], action),
                },
            };

        case TOPIC_FETCH_SUCCESS:
            hashtags = action.hashtags ? action.hashtags.join(",") : "";
            key = hashtagsAndQueryKey(hashtags, action.query);
            return {
                ...state,
                posts: {
                    ...state.posts,
                    [key]: postsReducer(state.posts[key], action),
                },
                isSubscribed: action.response.result.isSubscribed,
                isFavorite: action.response.result.isFavorite,
                counsellingId: action.response.result.counselling,
                counsellingPostsCount: action.response.result.counsellingPostsCount,
                brandDaysCampaignId: action.response.result.brandDaysCampaign,
                brandDaysCampaignGreetingText: action.response.result.brandDaysCampaignGreetingText,
                subscribers: action.response.result.subscribers,
                activeBrandDaysCampaignId: action.response.result.activeBrandDays.campaign,
                activeBrandDaysPostsIds: action.response.result.activeBrandDays.posts,
            };

        case TOPIC_SUBSCRIBE_SUCCESS:
        case TOPIC_UNSUBSCRIBE_SUCCESS:
            return tupdate(state, {
                isSubscribed: action.isSubscribed,
                subscribers: state.subscribers + (action.isSubscribed ? 1 : -1),
            });

        case FORUM_DELETE_TOPIC_API_SUCCESS:
            return tupdate(state, {
                isSubscribed: action.response.result.isSubscribed,
            });

        case TOPIC_SET_AS_FAVORITE_REQUEST:
            return tupdate(state, {
                isFavorite: action.setAsFavorite,
            });

        case TOPIC_SET_AS_FAVORITE_FAILURE:
            return tupdate(state, {
                isFavorite: !action.setAsFavorite,
            });

        default:
            return state;
    }
};

export interface TopicsState {
    [slug: string]: TopicState;
}

declare type TopicsReducerAction =
    | TopicFetchRequestAction
    | TopicFetchSuccessAction
    | TopicFetchFailureAction
    | TopicSubscribeSuccessAction
    | TopicUnsubscribeSuccessAction
    | TopicSetAsFavoriteRequestAction
    | TopicSetAsFavoriteSuccessAction
    | TopicSetAsFavoriteFailureAction;

const topicsReducer: Reducer<TopicsState, TopicsReducerAction> = (state = {}, action) => {
    switch (action.type) {
        case TOPIC_FETCH_SUCCESS:
        case TOPIC_SUBSCRIBE_SUCCESS:
        case TOPIC_UNSUBSCRIBE_SUCCESS:
        case TOPIC_SET_AS_FAVORITE_REQUEST:
        case TOPIC_SET_AS_FAVORITE_SUCCESS:
        case TOPIC_SET_AS_FAVORITE_FAILURE:
        case TOPIC_FETCH_REQUEST:
        case TOPIC_FETCH_FAILURE:
            return {
                ...state,
                [action.slug]: topicReducer(state[action.slug], action),
            };

        default:
            return state;
    }
};

export default topicsReducer;
